import { Box, Button, Container, Divider, Typography, Card, IconButton, Tooltip, Fab, Dialog, DialogTitle, DialogContent, TextField, DialogActions } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import { theme } from '../../theme';
import { AppBarPadding, BottomPadding } from '../../utils/helperUtils';
import LoadingComponent from '../components/LoadingComponent';
import { useParams, useNavigate } from 'react-router-dom';
import { getStorefrontPermits, uploadPermitServiceAuth } from '../../services/appServices';
import { useAuth } from '../../context/AuthContext';

import NoteAddIcon from '@mui/icons-material/NoteAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useSnackbar } from '../../context/SnackbarContext';

function ManageStorefrontPermit() {

  const { slug } = useParams();
  const { login, logout } = useAuth();
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingFullScreen, setLoadingFullScreen] = useState(false);
  const [existingPermits, setExistingPermits] = useState([])
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [description, setDescription] = useState("")
  const [inDialogloading, setInDialogLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {


        const response = await getStorefrontPermits(slug, login, logout);

        if (response.success) {
          setExistingPermits(response.data);
        } else {
          setExistingPermits([]);
          console.error("Failed to fetch permits:", response.error);
        }
      } catch (error) {
        console.error("Error fetching permits:", error);
        setError(error.message)
        setExistingPermits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  const onDescriptionDialogClose = () => {
    setDescriptionDialogOpen(false)
    setUploadedFile(null)
    setDescription("")
    setInDialogLoading(false)
  }

  const handlePermitUpload = async () => {

    if (!uploadedFile || !description) {
      console.warn("Missing file or description.");
      return;
    }

    setInDialogLoading(true);
    try {

      const response = await uploadPermitServiceAuth(
        {
          file: uploadedFile,
          description: description
        },
        slug,
        login,
        logout
      );


      if (!response.success) {
        setError(response.error || "Something went wrong while upload permit");
      } else {
        setTimeout(() => {
          onDescriptionDialogClose();
          setRefreshKey(prev => prev + 1);
          showSnackbar("🎉 Permit added successfully!", "success");
          setInDialogLoading(false);
        }, 3000);

      }
    } catch (err) {
      setError(err.message || "Unexpected error while uploading permit.");
      onDescriptionDialogClose();
    }
  }

  const FloatingActionButton = () => {
    const handleFileClick = () => {
      document.getElementById("permit-file-upload")?.click();
    };

    return (
      <>
        <input
          type="file"
          id="permit-file-upload"
          hidden
          accept=".pdf,image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setUploadedFile(file);
              setTimeout(() => setDescriptionDialogOpen(true), 0);
            }
          }}
        />
        <Tooltip title={"Add new Permit"} arrow>
          <Fab
            variant="extended"
            onClick={handleFileClick}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              backgroundColor: theme.colors.events.secondary,
              color: theme.colors.events.primaryDark,
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: theme.colors.events.primary,
              },
            }}
          >
            <NoteAddIcon sx={{ mr: 1 }} />
            Add New Permit
          </Fab>
        </Tooltip>
      </>
    );
  };


  return (
    <Box bgcolor={theme.colors.primary}>
      <Container maxWidth="xl" className="events-box-container">
        <AppBarPadding />
        <Grid container justifyContent={'center'}>
          <Grid size={{ md: 12, lg: 6 }} width={'100%'}>
            <Box display="flex" alignItems="center" mb={1}>
              <IconButton
              size='small'
                onClick={() => navigate(-1)}
                sx={{
                  color: theme.colors.events.secondary,
                  border: `1px solid ${theme.colors.events.secondary}`,
                  borderRadius: 3,
                  mr: 1
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <Typography fontSize={30} color={theme.colors.events.secondary} textAlign={'left'}>
                <Box sx={{ fontWeight: 'bold' }}>
                  My Permits 📄
                </Box>
              </Typography>
            </Box>
            <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary }} />
            {
              error && (
                <Box textAlign="center" mt={4} sx={{ padding: "0px 25px 0px 25px" }}>
                  <Typography color="error" variant="h6">⚠️ {error || 'Something went wrong, please try again later'}</Typography>
                </Box>
              )
            }
            {loading ? (
              <Box mt={1}>
                <LoadingComponent fullScreen={loadingFullScreen} />
              </Box>
            ) : (
              <Box mt={2} display="flex" flexDirection="column" gap={2}>
                {existingPermits.length === 0 ? (
                  <Typography color="white" textAlign="center">No permits uploaded yet.</Typography>
                ) : (
                  existingPermits.map((permit) => (
                    <Card
                      key={permit.id}
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 6,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        bgcolor: theme.colors.events.secondary
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={600}>{permit.description}</Typography>
                        {(() => {
                          const date = new Date(permit.uploaded_at);
                          const formattedDate = `${date.toLocaleDateString(undefined, {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })} at ${date.toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}`;
                          return (
                            <Typography variant="body2" color="text.secondary">
                              Added: {formattedDate}
                            </Typography>
                          );
                        })()}
                      </Box>
                      <IconButton
                        onClick={() => window.open(permit.file, '_blank')}
                        sx={{ borderRadius: 2 }}
                      >
                        <OpenInNewIcon sx={{ color: theme.colors.events.primary }} />
                      </IconButton>
                    </Card>
                  ))
                )}
              </Box>
            )
            }
          </Grid>
        </Grid>
        <FloatingActionButton />
        <Dialog open={descriptionDialogOpen} onClose={() => setDescriptionDialogOpen(false)} fullWidth>

          {inDialogloading ?
            <LoadingComponent fullScreen={false} textColor='black' />
            : <>
              <DialogTitle>Add Optional Description</DialogTitle>
              <DialogContent>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  label="Description"
                  fullWidth
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  error={description.trim() === ""}
                  helperText={description.trim() === "" ? "Description is required" : ""}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 4,
                    }
                  }}
                />
              </DialogContent>

              <DialogActions>
                <Button onClick={onDescriptionDialogClose}>Cancel</Button>
                <Button
                  sx={{ borderRadius: 3 }}
                  disableElevation
                  onClick={handlePermitUpload}
                  variant="contained"
                  disabled={description.trim() === ""}
                >
                  Submit
                </Button>
              </DialogActions>
            </>}
        </Dialog>
        <BottomPadding />
      </Container>
    </Box>
  )
}

export default ManageStorefrontPermit